'use client';

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';

interface SteamProfile {
  steamId: string;
  profile: {
    steamid: string;
    personaname: string;
    profileurl: string;
    avatar: string;
    avatarfull: string;
  } | null;
  authenticatedAt: string;
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [steamProfile, setSteamProfile] = useState<SteamProfile | null>(null);
  const auth = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Check Steam session on mount and update
    const checkSteamSession = () => {
      console.log('Header: Checking Steam session...');
      if (auth?.getSteamSession) {
        const session = auth.getSteamSession();
        console.log('Header: Steam session retrieved:', session);
        setSteamProfile(session);
      } else {
        console.log('Header: getSteamSession not available in auth context');
      }
    };
    
    // Initial check
    checkSteamSession();
    
    // Also check after a short delay to ensure LinkAccounts has processed the callback
    const timeoutId = setTimeout(checkSteamSession, 500);
    
    // Listen for storage changes (when user logs in/out in another tab)
    const handleStorageChange = () => {
      console.log('Header: Storage event detected');
      checkSteamSession();
    };
    
    // Listen for custom event (when user logs in/out in same tab)
    const handleSteamUpdate = (event: Event) => {
      console.log('Header: steam-session-updated event received', event);
      checkSteamSession();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('steam-session-updated', handleSteamUpdate);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('steam-session-updated', handleSteamUpdate);
    };
  }, [auth]);

  const baseNavLinks = [
    { label: 'Servers', href: '/home#servers' },
    { label: 'Leaderboards', href: '/leaderboards' },
    { label: 'Maps', href: '/maps' },
    { label: 'Store', href: '/home#supporter-perks' },
    { label: 'Rules', href: '/rules' },
    { label: 'Help', href: '/help' },
  ];

  // Only show Account link if Steam is connected
  const navLinks = steamProfile
    ? [...baseNavLinks, { label: 'Account', href: '/account' }]
    : baseNavLinks;

  console.log('Header render: steamProfile =', steamProfile, 'navLinks =', navLinks);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[rgba(11,11,15,0.95)] backdrop-blur-2xl border-b border-white/8 shadow-2xl'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        {/* Logo */}
        <a href="/home" className="flex items-center gap-3 group" aria-label="VANTA Home">
          {/* Geometric V icon */}
          <div className="relative w-9 h-9 flex items-center justify-center">
            <svg viewBox="0 0 32 32" fill="none" className="w-9 h-9" aria-hidden="true">
              <polygon
                points="16,4 30,28 2,28"
                fill="none"
                stroke="#6a5cff"
                strokeWidth="2.5"
                strokeLinejoin="round"
                className="transition-all duration-300 group-hover:drop-shadow-[0_0_10px_rgba(106,92,255,0.9)]"
              />
              <polygon
                points="16,11 24,28 8,28"
                fill="rgba(106,92,255,0.2)"
                stroke="none"
              />
            </svg>
          </div>
          <span
            className="font-heading text-2xl font-900 tracking-[0.15em] text-vanta-text group-hover:text-white transition-colors"
            style={{ fontWeight: 900, letterSpacing: '0.15em' }}
          >
            VANTA
          </span>
        </a>

        {/* Desktop Nav - Centered */}
        <div className="hidden lg:flex items-center gap-10">
          {navLinks?.map((link) => (
            <a key={link?.label} href={link?.href} className="nav-link text-base">
              {link?.label}
            </a>
          ))}
        </div>

        {/* Right Side - CTAs + Steam Profile */}
        <div className="hidden lg:flex items-center gap-4">
          {/* Steam Profile Bubble - Show before CTAs when connected */}
          {steamProfile && steamProfile.profile && (
            <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[rgba(106,92,255,0.15)] to-[rgba(106,92,255,0.08)] backdrop-blur-md border-2 border-vanta-primary/40 hover:border-vanta-primary/60 transition-all shadow-lg">
              <img
                src={steamProfile.profile.avatar}
                alt="Steam Avatar"
                className="w-7 h-7 rounded-full ring-2 ring-vanta-primary/60"
              />
              <span className="text-sm font-700 text-vanta-text" style={{ fontWeight: 700 }}>
                {steamProfile.steamId}
              </span>
            </div>
          )}
          
          <a
            href="https://discord.gg/gJgHMDZAES"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary px-6 py-3 text-sm flex items-center gap-2.5 rounded-xl"
            aria-label="Join VANTA Discord"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
            </svg>
            Discord
          </a>
          <a
            href="steam://connect/play.vanta.gg"
            className="btn-primary-outlined px-6 py-3 text-sm rounded-xl font-700"
            style={{ fontWeight: 700 }}
            aria-label="Join VANTA Server"
          >
            Join Server
          </a>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="lg:hidden text-vanta-muted hover:text-white transition-colors p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
        >
          <Icon name={menuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} />
        </button>
      </nav>
      {/* Mobile Menu */}
      {menuOpen && (
        <div className="lg:hidden glass border-t border-white/8">
          <div className="px-6 py-6 flex flex-col gap-4">
            {navLinks?.map((link) => (
              <a
                key={link?.label}
                href={link?.href}
                className="text-vanta-muted hover:text-white text-base font-semibold uppercase tracking-widest transition-colors py-2"
                onClick={() => setMenuOpen(false)}
              >
                {link?.label}
              </a>
            ))}
            
            {/* Steam Profile Bubble - Mobile */}
            {steamProfile && steamProfile.profile && (
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-[rgba(106,92,255,0.15)] to-[rgba(106,92,255,0.08)] backdrop-blur-md border-2 border-vanta-primary/40">
                <img
                  src={steamProfile.profile.avatar}
                  alt="Steam Avatar"
                  className="w-9 h-9 rounded-full ring-2 ring-vanta-primary/60"
                />
                <div className="flex flex-col">
                  <span className="text-sm font-700 text-vanta-text" style={{ fontWeight: 700 }}>
                    {steamProfile.profile.personaname}
                  </span>
                  <span className="text-xs text-vanta-muted">
                    {steamProfile.steamId}
                  </span>
                </div>
              </div>
            )}
            
            <div className="flex flex-col gap-3 pt-4 border-t border-white/8">
              <a
                href="https://discord.gg/gJgHMDZAES"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary px-5 py-3 text-sm text-center rounded-xl"
              >
                Join Discord
              </a>
              <a
                href="steam://connect/play.vanta.gg"
                className="btn-primary-outlined px-5 py-3 text-sm text-center rounded-xl"
              >
                Join Server
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}